import { useEffect, useRef, useState } from 'react';
import { confirm } from '../utils/confirm';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { runSync, pingExtension } from '../utils/extBridge';
import { fmtDateTime } from '../utils/format';
import { useSync } from '../context/SyncContext';

import client from '../api/client';
import {
  socsonStatus,
  setSocsonToken,
  socsonRelogin,
  socsonTest,
  syncStaffInfo,
  getScanSchedule,
  saveScanSchedule,
  setScanSource,
  getScanStatus,
  resetScanJob,
  getSupportRevenue,
  scanOrdersServer,
  scanDepositsServer,
  scanRevenuesServer,
  scanQuotesAllServer,
  scanCustomersServer,
  scanCustomerDetailsServer,
  scanWeeklyDepositsServer,
  scanBackfillDepositsServer,
  scanBackfillKpiServer,
} from '../api/socson';
import { pmboxStatus, setPmboxToken, pmboxTest, pmboxRelogin, scanPmboxCustomers, syncPmboxStaff } from '../api/pmbox';

// Nội dung token SocSon + thao tác chạy 1 lần (test/cập nhật hồ sơ NV).
// KHÔNG tự bọc .card — được đặt trong thẻ gộp "Token SocSon + Extension" (cột trái).
function SocsonServerCard() {
  const toast = useToast();
  const [st, setSt] = useState(null);
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [syncingStaff, setSyncingStaff] = useState(false);
  const [staffRes, setStaffRes] = useState(null);
  const [relogging, setRelogging] = useState(false);
  const abortRef = useRef(null); // huỷ thao tác đang chạy

  const load = () => socsonStatus().then(setSt).catch(() => {});
  useEffect(() => { load(); }, []);

  const isCancel = (e) => e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';
  function stop() {
    abortRef.current?.abort();
  }

  async function save() {
    if (!token.trim()) return toast('Dán token SocSon', 'warn');
    setSaving(true);
    try {
      await setSocsonToken(token.trim());
      setToken('');
      toast('Đã lưu token SocSon');
      load();
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi lưu token', 'danger');
    } finally { setSaving(false); }
  }
  async function relogin() {
    setRelogging(true);
    try {
      const r = await socsonRelogin();
      toast(r.message || 'Đã gia hạn token');
      load();
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi gia hạn token', 'danger');
    } finally { setRelogging(false); }
  }
  async function test() {
    const ac = new AbortController(); abortRef.current = ac;
    setTesting(true); setResult(null);
    try {
      const r = await socsonTest(ac.signal);
      setResult(r);
    } catch (e) {
      if (isCancel(e)) toast('Đã dừng', 'warn');
      else setResult({ ok: false, error: e.response?.data?.message || 'Lỗi gọi test' });
    } finally { setTesting(false); abortRef.current = null; }
  }
  async function doSyncStaff() {
    const ac = new AbortController(); abortRef.current = ac;
    setSyncingStaff(true); setStaffRes(null);
    try {
      const r = await syncStaffInfo(ac.signal);
      setStaffRes(r);
      toast(`Đã cập nhật ${r.updated}/${r.matched} hồ sơ nhân viên`);
    } catch (e) {
      if (isCancel(e)) toast('Đã dừng', 'warn');
      else toast(e.response?.data?.message || 'Lỗi cập nhật hồ sơ nhân viên', 'danger');
    } finally { setSyncingStaff(false); abortRef.current = null; }
  }

  return (
    <div>
      <h3>🔑 Token SocSon (server-side)</h3>
      <div className="small muted" style={{ marginTop: -6, marginBottom: 8 }}>
        Lấy token từ tab socsonship.com (đã đăng nhập): mở Console gõ <code>localStorage.getItem('token')</code> rồi dán vào đây.
        Toàn bộ các nút quét đều chạy trên SERVER bằng token này.
      </div>
      {st?.hasToken && st?.tokenExp && (
        st.tokenExpired ? (
          <div className="small" style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--danger-bg, #fde8e8)', color: 'var(--danger)', fontWeight: 600 }}>
            ⛔ Token đã HẾT HẠN lúc {fmtDateTime(st.tokenExp)} — mọi lệnh quét tự động đang lỗi 401. Hãy dán token mới ngay.
          </div>
        ) : (
          (() => {
            const hLeft = (new Date(st.tokenExp) - Date.now()) / 3600000;
            const warn = hLeft < 12;
            return (
              <div className="small" style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 8, background: warn ? 'var(--warn-bg, #fff4e0)' : 'var(--panel2)', color: warn ? 'var(--warn)' : 'var(--muted)', fontWeight: warn ? 600 : 400 }}>
                {warn ? '⚠️ ' : '✅ '}Token còn hạn đến <b>{fmtDateTime(st.tokenExp)}</b> (~{Math.floor(hLeft)}h nữa){st.tokenOwner ? ` · TK: ${st.tokenOwner}` : ''}
                {warn ? ' — nên dán token mới sớm để quét không gián đoạn.' : ''}
              </div>
            );
          })()
        )
      )}
      <div className="field">
        <label>Token SocSon {st?.hasToken ? `(đã lưu · cập nhật ${st.lastTokenAt ? fmtDateTime(st.lastTokenAt) : ''})` : '(chưa có)'}</label>
        <textarea rows={2} value={token} onChange={(e) => setToken(e.target.value)} placeholder="Dán token (Bearer eyJ...) — station tự suy ra" />
      </div>
      <div className="row" style={{ gap: 8 }}>
        {st?.station && <span className="small muted">Station: {st.station}</span>}
        <div className="spacer" />
        <button className="btn ghost sm" onClick={test} disabled={testing || !st?.hasToken}>
          {testing ? 'Đang test…' : 'Test gọi API'}
        </button>
        {testing && <button className="btn sm danger" onClick={stop}>■ Dừng</button>}
        <button className="btn sm" onClick={save} disabled={saving}>{saving ? 'Đang lưu…' : 'Lưu token'}</button>
      </div>

      {/* Token TỰ GIA HẠN: server tự làm mới token trước khi hết hạn (khỏi dán lại) */}
      <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0 10px' }} />
      <div className="small muted" style={{ marginBottom: 8 }}>
        <b>🔄 Token tự gia hạn</b> — chỉ cần <b>dán token 1 lần</b>, server tự làm mới token (qua phiên SocSon)
        trước khi hết hạn (khi còn &lt;18h) → token sống mãi, khỏi dán lại. Nếu để token <b>hết hẳn</b> mới cần dán mới.
        {st?.lastLoginAt && <span style={{ color: 'var(--ok)' }}> · Gia hạn gần nhất: {fmtDateTime(st.lastLoginAt)}</span>}
      </div>
      {st?.hasToken && !st?.tokenExpired && (
        <button className="btn ghost sm" onClick={relogin} disabled={relogging}>{relogging ? 'Đang gia hạn…' : '↻ Gia hạn token ngay'}</button>
      )}
      {result && (
        <div className="small" style={{ marginTop: 8, color: result.ok ? 'var(--ok)' : 'var(--danger)' }}>
          {result.ok
            ? `✓ Server gọi API SocSon OK — HTTP ${result.status}, tổng đơn ~${result.total ?? '?'}, đơn mẫu ${result.sample || '—'}`
            : `⚠️ Thất bại — HTTP ${result.status ?? 0}${result.error ? ' · ' + result.error : ''} (token hết hạn / sai, hoặc SocSon chặn server)`}
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0 10px' }} />
      <div className="row between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="small muted">
          <b>Cập nhật hồ sơ nhân viên</b> — đồng bộ họ tên, SĐT, email, ngày sinh từ SocSon theo <b>mã NV (staff_code)</b>. Không đụng đăng nhập / vai trò.
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button className="btn ghost sm" onClick={doSyncStaff} disabled={syncingStaff || !st?.hasToken}>
            {syncingStaff ? 'Đang cập nhật…' : '👥 Cập nhật hồ sơ NV'}
          </button>
          {syncingStaff && <button className="btn sm danger" onClick={stop}>■ Dừng</button>}
        </div>
      </div>
      {staffRes && (
        <div className="small" style={{ marginTop: 8 }}>
          ✓ {staffRes.totalStaff} NV SocSon · khớp <b>{staffRes.matched}</b> · cập nhật <b style={{ color: 'var(--accent2)' }}>{staffRes.updated}</b>
          {staffRes.unmatchedCount > 0 && <span className="muted"> · {staffRes.unmatchedCount} mã không khớp funytask</span>}
          {!!(staffRes.changed || []).length && (
            <div className="muted" style={{ marginTop: 4 }}>
              Đã sửa: {staffRes.changed.slice(0, 12).map((c) => c.name).join(', ')}{staffRes.changed.length > 12 ? '…' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Mặc định ngày: đơn hàng cần ≥14 ngày chẵn -> 14 ngày gần nhất; dự kiến -> đầu tháng đến nay.
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
const thirtyDaysAgo = new Date(Date.now() - 29 * 86400000); // 30 ngày gần nhất (gồm hôm nay)

// Định nghĩa các nút quét — TẤT CẢ chạy server-side (dùng token đã lưu, không cần tab/extension),
// chạy NỀN trên server (backend/src/cron.js lo lịch tự động; nút ở đây chỉ để chạy ngay khi cần).
// group 'manual' = lọc theo khoảng ngày, chạy khi cần. group 'auto' = đã có lịch tự động trên server.
const ACTIONS = [
  { id: 'orders', label: 'Đẩy đơn hàng (so sánh tuần)', date: true, group: 'manual', allowTP: true, jobKey: 'orders', run: (d) => scanOrdersServer(d.from, d.to), note: 'Khoảng ngày ≥ 14 ngày, số chẵn (chia đôi tuần trước / tuần này). Tạo đợt ở Danh sách quét.' },
  { id: 'deposits', label: 'Đẩy tỷ lệ cọc', date: true, group: 'manual', allowTP: true, jobKey: 'deposits', run: (d) => scanDepositsServer(d.from, d.to), note: 'Tạo đợt KH độc lập cho màn Danh sách quét.' },
  { id: 'revenues', label: 'Đẩy báo cáo doanh thu', date: true, group: 'auto', jobKey: 'revenues', autoKey: 'dailyRevenue', run: (d) => scanRevenuesServer(d.from, d.to), note: 'Hiện ở Trang chủ + cập nhật DT thực tế KPI, kèm chốt KPI tháng. Tự động chạy trong KHUNG ĐÊM 01:00–07:00 (giờ VN); lỡ giờ đêm thì chạy bù ban ngày.' },
  { id: 'supportRevenue', label: 'Quét doanh thu hỗ trợ', date: true, group: 'auto', jobKey: 'supportRevenue', run: (d) => client.post('/socson/scan-support', d).then((r) => r.data), note: 'Tính cho nhân viên KD; phần trưởng phòng quy về người hỗ trợ (leader). Tự động trong khung đêm 01:00–07:00; lỡ giờ thì chạy bù ban ngày.' },
  { id: 'quotesAll', label: 'Đẩy tổng đơn + doanh thu dự kiến', date: true, group: 'auto', jobKey: 'quotesAll', run: (d) => scanQuotesAllServer(d.from, d.to), note: 'Quét 1 LẦN rồi cập nhật CẢ báo cáo mua hàng LẪN cột DT dự kiến ở KPI. Lấy 30 ngày gần nhất. Tự động trong khung đêm 01:00–07:00; lỡ giờ đêm thì CHẠY BÙ ban ngày (nên đôi khi Báo cáo mua hàng cập nhật vào buổi tối).' },
  { id: 'weekly', label: 'Tỷ lệ cọc tháng này', date: false, group: 'auto', jobKey: 'weeklyDeposits', run: () => scanWeeklyDepositsServer(), note: 'Cập nhật tỷ lệ cọc tháng hiện tại cho cột Tỷ lệ cọc ở KPI. Tự động 1 lần/tuần (chạy trong khung đêm 01:00–07:00).' },
  { id: 'customers', label: 'Đẩy danh sách KH (toàn bộ)', date: false, group: 'auto', jobKey: 'customers', run: () => scanCustomersServer(), note: 'Quét & merge toàn bộ KH (giữ dữ liệu cũ nếu SocSon trả rỗng cho 1 trường). Tự động mỗi ngày trong khung đêm 01:00–07:00; lỡ thì chạy bù ban ngày.' },
  { id: 'details', label: 'Lấy chi tiết KH (ngầm)', date: false, group: 'auto', jobKey: 'details', run: () => scanCustomerDetailsServer(), note: 'Lấy doanh thu tháng / khiếu nại / trao đổi / chuyển sale cho từng KH (chạy nền). Tự động trong khung đêm 01:00–07:00; lỡ thì chạy bù ban ngày.' },
  { id: 'backfill', label: 'Backfill KPI (từ đầu năm)', date: false, group: 'manual', gdOnly: true, jobKey: 'backfillKpi', run: () => scanBackfillKpiServer(), note: 'Lấy doanh thu từ tháng 1 năm nay → hiện tại vào lịch sử KPI (cho biểu đồ Trang chủ). KHÔNG tự chạy — chỉ Giám đốc bấm tay.' },
  { id: 'depositsMonthly', label: 'Backfill tỷ lệ cọc 6 tháng', date: false, group: 'manual', gdOnly: true, jobKey: 'backfillDeposits', run: () => scanBackfillDepositsServer(), note: 'Lấy tỷ lệ cọc 6 tháng gần nhất theo từng tháng vào lịch sử KPI. KHÔNG tự chạy — chỉ Giám đốc bấm tay.' },
];

// Poll trạng thái các job quét server-side (ScanJobStatus) + riêng doanh thu hỗ trợ
// (SupportRevenueReport có status field khác) mỗi vài giây để hiển thị đang chạy/xong/lỗi.
function useScanStatuses() {
  const [map, setMap] = useState({});
  const [support, setSupport] = useState(null);
  useEffect(() => {
    let alive = true;
    const poll = () => {
      getScanStatus().then((m) => { if (alive) setMap(m || {}); }).catch(() => {});
      getSupportRevenue().then((r) => { if (alive) setSupport(r); }).catch(() => {});
    };
    poll();
    const iv = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  return { map, support };
}

// Thẻ cấu hình LỊCH QUÉT TỰ ĐỘNG: mỗi thao tác bật/tắt + đặt chu kỳ (phút/giờ).
function ScanScheduleCard() {
  const toast = useToast();
  const [jobs, setJobs] = useState(null);
  const [yahoo, setYahoo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanSrc, setScanSrc] = useState('pmbox');
  const [srcSaving, setSrcSaving] = useState(false);
  useEffect(() => {
    getScanSchedule()
      .then((d) => { setJobs(d.jobs); setYahoo(d.yahoo || { enabled: true, autoHour: 6, tpFromHour: 8 }); if (d.scanSource) setScanSrc(d.scanSource); })
      .catch(() => setJobs([]));
  }, []);
  async function saveSrc(next) {
    const prev = scanSrc;
    setScanSrc(next);
    setSrcSaving(true);
    try {
      await setScanSource(next);
      toast(`Đã đổi nguồn quét chính sang ${next.toUpperCase()}`);
    } catch (e) {
      setScanSrc(prev);
      toast(e.response?.data?.message || 'Lỗi đổi nguồn quét', 'danger');
    } finally { setSrcSaving(false); }
  }
  const setJob = (id, patch) => setJobs((js) => js.map((j) => (j.id === id ? { ...j, ...patch } : j)));
  async function save() {
    setSaving(true);
    try {
      const payload = {};
      jobs.forEach((j) => (payload[j.id] = { enabled: j.enabled, everyMinutes: Number(j.everyMinutes) || j.defaultEvery }));
      await saveScanSchedule(payload, yahoo);
      toast('Đã lưu lịch quét tự động');
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi lưu lịch', 'danger');
    } finally { setSaving(false); }
  }
  const HOURS = Array.from({ length: 24 }, (_, i) => i);
  // Hiển thị chu kỳ gọn: phút -> "N phút" / "N giờ" / "N ngày".
  const human = (m) => (m % 1440 === 0 ? `${m / 1440} ngày` : m % 60 === 0 ? `${m / 60} giờ` : `${m} phút`);
  return (
    <div>
      <h3>⏱️ Lịch quét tự động</h3>
      <div className="small muted" style={{ marginTop: -6, marginBottom: 8 }}>
        Server tự chạy theo chu kỳ bên dưới, vào <b>ban đêm 01:00–07:00 giờ VN</b> (nghỉ ban ngày để giảm tải). Tắt cái không cần.
      </div>

      {/* NGUỒN QUÉT CHÍNH: mọi lệnh quét (đơn/doanh thu/cọc/công nợ/KH…) không chỉ định nguồn sẽ lấy từ đây. */}
      <div className="card" style={{ marginBottom: 12, background: 'var(--surface-2, rgba(99,102,241,.06))' }}>
        <div className="row between" style={{ flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <div>
            <b>🎯 Nguồn quét chính</b>
            <div className="small muted">Toàn bộ lệnh quét (đơn hàng, doanh thu, cọc, công nợ, KH, chi tiết KH, lương) sẽ lấy dữ liệu từ nguồn này.</div>
          </div>
          <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            Nguồn:
            <select value={scanSrc} disabled={srcSaving} onChange={(e) => saveSrc(e.target.value)}>
              <option value="pmbox">pmbox (mặc định)</option>
              <option value="socson">socson</option>
            </select>
          </label>
        </div>
        <div className="small muted" style={{ marginTop: 6 }}>
          Đang quét từ: <b>{scanSrc.toUpperCase()}</b> — hãy đảm bảo đã dán token còn hạn cho nguồn này{scanSrc === 'pmbox' ? ' (ô token pmbox bên dưới)' : ' (ô token SocSon ở trên)'}.
        </div>
      </div>
      {!jobs ? (
        <div className="empty">Đang tải…</div>
      ) : (
        <>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Thao tác</th>
                  <th className="c">Bật</th>
                  <th className="r">Chu kỳ (phút)</th>
                  <th>≈</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id}>
                    <td>{j.label}</td>
                    <td className="c">
                      <input type="checkbox" checked={j.enabled} onChange={(e) => setJob(j.id, { enabled: e.target.checked })} />
                    </td>
                    <td className="r">
                      <input type="number" min="5" step="5" style={{ width: 90, textAlign: 'right' }} value={j.everyMinutes}
                        onChange={(e) => setJob(j.id, { everyMinutes: e.target.value })} disabled={!j.enabled} />
                    </td>
                    <td className="small muted">{j.enabled ? `mỗi ${human(Number(j.everyMinutes) || j.defaultEvery)}` : 'tắt'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {yahoo && (
            <div className="card" style={{ marginTop: 12 }}>
              <div className="row between" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <b>⏰ Quét hạn đơn Yahoo</b>
                <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={yahoo.enabled} onChange={(e) => setYahoo({ ...yahoo, enabled: e.target.checked })} /> Tự quét hằng ngày
                </label>
              </div>
              <div className="row" style={{ gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  Giờ hệ thống tự quét:
                  <select value={yahoo.autoHour} disabled={!yahoo.enabled} onChange={(e) => setYahoo({ ...yahoo, autoHour: Number(e.target.value) })}>
                    {HOURS.map((h) => <option key={h} value={h}>{h}h</option>)}
                  </select>
                </label>
                <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  Trưởng phòng quét thêm từ:
                  <select value={yahoo.tpFromHour} onChange={(e) => setYahoo({ ...yahoo, tpFromHour: Number(e.target.value) })}>
                    {HOURS.map((h) => <option key={h} value={h}>{h}h</option>)}
                  </select>
                </label>
              </div>
              <div className="small muted" style={{ marginTop: 6 }}>
                Hệ thống tự quét lúc <b>{yahoo.autoHour}h</b> (từ từ, chỉ tải đơn mới). Trưởng phòng bấm quét thêm từ <b>{yahoo.tpFromHour}h</b> trở đi — tránh chồng chéo. Giám đốc quét bất cứ lúc nào.
              </div>
            </div>
          )}

          <div className="row" style={{ marginTop: 10 }}>
            <div className="spacer" />
            <button className="btn" onClick={save} disabled={saving}>{saving ? 'Đang lưu…' : 'Lưu lịch'}</button>
          </div>
        </>
      )}
    </div>
  );
}

// NGUỒN pmbox.me (clone SocSon) — dán token riêng + quét khách hàng pmbox. Dữ liệu gộp chung Funny,
// gắn nhãn nguồn 'pmbox'. Gọn hơn card SocSon (Phase 1: mới quét Khách hàng).
function PmboxServerCard() {
  const toast = useToast();
  const [st, setSt] = useState(null);
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [relogging, setRelogging] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [job, setJob] = useState(null); // trạng thái job 'pmbox:customers'
  const [syncingStaff, setSyncingStaff] = useState(false);
  const [staffRes, setStaffRes] = useState(null);
  const abortRef = useRef(null);

  const load = () => pmboxStatus().then(setSt).catch(() => {});
  const loadJob = () => getScanStatus().then((all) => setJob((all || []).find((j) => j.key === 'pmbox:customers') || null)).catch(() => {});
  useEffect(() => { load(); loadJob(); }, []);
  // Poll trạng thái khi đang quét (job chạy nền trên server).
  useEffect(() => {
    if (!scanning) return;
    const t = setInterval(loadJob, 3000);
    return () => clearInterval(t);
  }, [scanning]);
  useEffect(() => { if (job && job.status !== 'running') setScanning(false); }, [job]);

  const isCancel = (e) => e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';

  async function save() {
    if (!token.trim()) return toast('Dán token pmbox', 'warn');
    setSaving(true);
    try {
      await setPmboxToken(token.trim());
      setToken('');
      toast('Đã lưu token pmbox');
      load();
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi lưu token', 'danger');
    } finally { setSaving(false); }
  }
  async function relogin() {
    setRelogging(true);
    try {
      const r = await pmboxRelogin();
      toast(r.message || 'Đã gia hạn token');
      load();
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi gia hạn token', 'danger');
    } finally { setRelogging(false); }
  }
  async function test() {
    const ac = new AbortController(); abortRef.current = ac;
    setTesting(true); setResult(null);
    try {
      setResult(await pmboxTest(ac.signal));
    } catch (e) {
      if (isCancel(e)) toast('Đã dừng', 'warn');
      else setResult({ ok: false, error: e.response?.data?.message || 'Lỗi gọi test' });
    } finally { setTesting(false); abortRef.current = null; }
  }
  async function scanCustomers() {
    setScanning(true);
    try {
      const r = await scanPmboxCustomers();
      toast(r?.alreadyRunning ? 'Đang quét rồi…' : 'Đã bắt đầu quét khách hàng pmbox (chạy nền)');
      loadJob();
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi bắt đầu quét', 'danger');
      setScanning(false);
    }
  }
  async function doSyncStaff() {
    const ac = new AbortController(); abortRef.current = ac;
    setSyncingStaff(true); setStaffRes(null);
    try {
      const r = await syncPmboxStaff(ac.signal);
      setStaffRes(r);
      toast(`Đã cập nhật ${r.updated}/${r.matched} hồ sơ nhân viên`);
    } catch (e) {
      if (isCancel(e)) toast('Đã dừng', 'warn');
      else toast(e.response?.data?.message || 'Lỗi cập nhật hồ sơ nhân viên', 'danger');
    } finally { setSyncingStaff(false); abortRef.current = null; }
  }

  return (
    <div>
      <h3>🟣 Token pmbox.me (server-side)</h3>
      <div className="small muted" style={{ marginTop: -6, marginBottom: 8 }}>
        Nguồn thứ 2 (clone SocSon). Lấy token từ tab pmbox.me (đã đăng nhập): Console gõ <code>localStorage.getItem('token')</code> rồi dán vào đây. KH pmbox sẽ gộp chung, gắn nhãn nguồn <b>pmbox</b>.
      </div>
      {st?.hasToken && st?.tokenExp && (
        st.tokenExpired ? (
          <div className="small" style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--danger-bg, #fde8e8)', color: 'var(--danger)', fontWeight: 600 }}>
            ⛔ Token pmbox đã HẾT HẠN lúc {fmtDateTime(st.tokenExp)} — hãy dán token mới.
          </div>
        ) : (
          <div className="small" style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--panel2)', color: 'var(--muted)' }}>
            ✅ Token pmbox còn hạn đến <b>{fmtDateTime(st.tokenExp)}</b>{st.tokenOwner ? ` · TK: ${st.tokenOwner}` : ''}
          </div>
        )
      )}
      <div className="field">
        <label>Token pmbox {st?.hasToken ? `(đã lưu · ${st.lastTokenAt ? fmtDateTime(st.lastTokenAt) : ''})` : '(chưa có)'}</label>
        <textarea rows={2} value={token} onChange={(e) => setToken(e.target.value)} placeholder="Dán token (Bearer eyJ...) — station tự suy ra" />
      </div>
      <div className="row" style={{ gap: 8 }}>
        {st?.station && <span className="small muted">Station: {st.station}</span>}
        <div className="spacer" />
        <button className="btn ghost sm" onClick={test} disabled={testing || !st?.hasToken}>{testing ? 'Đang test…' : 'Test gọi API'}</button>
        {st?.hasToken && !st?.tokenExpired && <button className="btn ghost sm" onClick={relogin} disabled={relogging}>{relogging ? '…' : '↻ Gia hạn'}</button>}
        <button className="btn sm" onClick={save} disabled={saving}>{saving ? 'Đang lưu…' : 'Lưu token'}</button>
      </div>
      {result && (
        <div className="small" style={{ marginTop: 8, color: result.ok ? 'var(--ok)' : 'var(--danger)' }}>
          {result.ok
            ? `✓ Gọi API pmbox OK — HTTP ${result.status}, tổng ~${result.total ?? '?'}`
            : `⚠️ Thất bại — HTTP ${result.status ?? 0}${result.error ? ' · ' + result.error : ''}`}
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0 10px' }} />
      <div className="row between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="small muted"><b>Quét khách hàng pmbox</b> — kéo toàn bộ danh sách KH pmbox về, gắn nhãn nguồn pmbox.</div>
        <button className="btn sm" onClick={scanCustomers} disabled={scanning || !st?.hasToken}>
          {scanning ? 'Đang quét…' : '⟳ Quét khách hàng'}
        </button>
      </div>
      <div className="row between" style={{ flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        <div className="small muted"><b>Quét nhân viên pmbox</b> — khớp theo mã NV để map sale → NV (cần cho chat với KH pmbox).</div>
        <button className="btn ghost sm" onClick={doSyncStaff} disabled={syncingStaff || !st?.hasToken}>
          {syncingStaff ? 'Đang quét…' : '⟳ Quét nhân viên'}
        </button>
      </div>
      {staffRes && (
        <div className="small muted" style={{ marginTop: 6 }}>
          Khớp {staffRes.matched}/{staffRes.totalStaff} NV · cập nhật {staffRes.updated}
          {staffRes.unmatchedCount ? ` · ${staffRes.unmatchedCount} mã NV không khớp` : ''}
        </div>
      )}
      {job && (
        <div className="small" style={{ marginTop: 6, color: job.status === 'error' ? 'var(--danger)' : job.status === 'done' ? 'var(--ok)' : 'var(--muted)' }}>
          {job.status === 'running' ? '⏳ Đang quét…' : job.status === 'done' ? `✓ ${job.message || 'Xong'}` : job.status === 'error' ? `⚠️ ${job.lastError || 'Lỗi'}` : ''}
        </div>
      )}
    </div>
  );
}

export default function SyncPage() {
  const toast = useToast();
  const { running, progress, lastResult, startSync, stopSync } = useSync();
  const [installed, setInstalled] = useState(null); // null=checking
  const [apiBase] = useState((import.meta.env.VITE_API_URL || 'https://funnytask.net/api').replace(/\/api$/i, ''));
  const { me } = useAuth();
  const isAdmin = me.role === 'GD' || me.role === 'CV'; // GĐ/CV: toàn quyền cấu hình; TP KD: chỉ quét đơn
  const { map: statusMap, support: supportStatus } = useScanStatuses();
  const [sending, setSending] = useState(null); // actionId đang gửi yêu cầu (chờ server nhận)
  const [syncKey, setSyncKey] = useState('');
  const [configSaved, setConfigSaved] = useState(false);
  // Mỗi nút (loại cần ngày) có khoảng ngày riêng.
  const [dateMap, setDateMap] = useState({
    orders: { from: ymd(firstOfMonth), to: ymd(today) },
    deposits: { from: ymd(firstOfMonth), to: ymd(today) },
    revenues: { from: ymd(firstOfMonth), to: ymd(today) },
    supportRevenue: { from: ymd(firstOfMonth), to: ymd(today) }, // doanh thu hỗ trợ: từ đầu tháng đến hôm nay
    quotesAll: { from: ymd(thirtyDaysAgo), to: ymd(today) }, // gộp: tổng đơn + DT dự kiến, 30 ngày gần nhất
  });
  const setDate = (id, field, val) => setDateMap((m) => ({ ...m, [id]: { ...m[id], [field]: val } }));
  const [lastRuns, setLastRuns] = useState({}); // { actionId: { at, byName } }

  const loadLastRuns = () => client.get('/sync-jobs/last').then((r) => setLastRuns(r.data || {})).catch(() => {});

  useEffect(() => {
    pingExtension().then(setInstalled);
    loadLastRuns();
  }, []);

  // "Lần cuối" chuẩn = mốc MỚI NHẤT trong: trạng thái quét (ScanJobStatus, cả tay + AUTO đều ghi
  // finishedAt/byName), job auto tương ứng (autoKey nếu key auto khác jobKey), và SyncLog (bấm tay cũ).
  function lastRunOf(a) {
    const cands = [];
    // Ưu tiên mốc quét THÀNH CÔNG gần nhất (lastSuccessAt); chưa có thì mới lấy finishedAt (kể cả lỗi).
    const push = (s) => { const at = s && (s.lastSuccessAt || s.finishedAt); if (at) cands.push({ at, byName: s.byName || '' }); };
    push(statusMap[a.jobKey]);
    if (a.autoKey) push(statusMap[a.autoKey]);
    // Doanh thu hỗ trợ dùng model riêng: mốc = syncedAt, người chạy = updatedByName.
    if (a.id === 'supportRevenue' && supportStatus?.syncedAt) cands.push({ at: supportStatus.syncedAt, byName: supportStatus.updatedByName || '' });
    if (lastRuns[a.id]?.at) cands.push({ at: lastRuns[a.id].at, byName: lastRuns[a.id].byName || '' });
    if (!cands.length) return null;
    return cands.reduce((best, c) => (new Date(c.at) > new Date(best.at) ? c : best));
  }

  function getRowStatus(a) {
    if (a.id === 'supportRevenue') {
      if (!supportStatus) return null;
      return { status: supportStatus.status || 'idle', lastError: supportStatus.lastError };
    }
    return statusMap[a.jobKey] || null;
  }

  async function doAction(a) {
    if (sending) return;
    setSending(a.id);
    try {
      const params = a.date ? { ...dateMap[a.id] } : {};
      const r = await a.run(params);
      toast(r?.message || 'Đã bắt đầu quét ở nền…');
      client.post('/sync-jobs/mark', { action: a.id }).catch(() => {});
      loadLastRuns();
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi khi bắt đầu quét', 'danger');
    } finally {
      setSending(null);
    }
  }

  // Hủy job đang "treo" ngay lập tức (không cần chờ 15 phút tự mở khoá) để bấm Chạy lại.
  async function doReset(a) {
    if (!(await confirm(`Hủy đợt quét "${a.label}" đang chạy nền và cho phép chạy lại ngay?`))) return;
    try {
      await resetScanJob(a.jobKey);
      toast('Đã hủy — có thể bấm Chạy lại.');
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi khi hủy', 'danger');
    }
  }

  async function saveConfig() {
    if (!syncKey.trim()) return toast('Nhập Sync key', 'warn');
    const res = await runSync('set_config', { url: apiBase, key: syncKey.trim() });
    if (res.ok) {
      setConfigSaved(true);
      toast('Đã lưu cấu hình vào extension');
    } else {
      toast(res.error || 'Lỗi lưu cấu hình', 'danger');
    }
  }

  // Render 1 dòng nút quét — TẤT CẢ chạy server-side, nền, không cần tab/extension.
  const renderAction = (a) => {
    if (a.gdOnly && me.role !== 'GD') return null; // backfill: chỉ Giám đốc thấy & chạy
    const rs = getRowStatus(a);
    const isRunning = rs?.status === 'running';
    return (
      <div key={a.id} style={{ padding: '12px 0', borderTop: '1px solid var(--border)' }}>
        <div className="row between" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 600 }}>{a.label}</div>
            <div className="small muted">{a.note}</div>
            {(() => { const lr = lastRunOf(a); return (
            <div className="small" style={{ marginTop: 2, color: lr?.at ? 'var(--muted)' : 'var(--border)' }}>
              🕒 Quét thành công gần nhất: {lr?.at ? fmtDateTime(lr.at) : 'chưa chạy'}
              {lr?.byName ? ` · ${lr.byName}` : ''}
            </div>
            ); })()}
          </div>
          {a.date && (
            <div className="row" style={{ gap: 6 }}>
              <input
                type="date"
                style={{ width: 'auto' }}
                value={dateMap[a.id].from}
                onChange={(e) => setDate(a.id, 'from', e.target.value)}
                onClick={(e) => e.currentTarget.showPicker?.()}
              />
              <span className="small muted">→</span>
              <input
                type="date"
                style={{ width: 'auto' }}
                value={dateMap[a.id].to}
                onChange={(e) => setDate(a.id, 'to', e.target.value)}
                onClick={(e) => e.currentTarget.showPicker?.()}
              />
            </div>
          )}
          <button className="btn sm" onClick={() => doAction(a)} disabled={sending === a.id || isRunning}>
            {sending === a.id ? 'Đang gửi…' : isRunning ? 'Đang chạy nền…' : 'Chạy'}
          </button>
          {isRunning && (
            <button className="btn sm ghost" onClick={() => doReset(a)} title="Hủy ngay nếu bị treo, không cần chờ 15 phút">
              ✕ Hủy
            </button>
          )}
        </div>
        {isRunning && (
          <div className="small" style={{ marginTop: 6, color: 'var(--accent)' }}>
            Đang quét ở nền trên server — có thể rời trang, kết quả tự cập nhật sau ít phút. Nếu bị treo lâu, bấm "✕ Hủy" để chạy lại ngay.
          </div>
        )}
        {!isRunning && rs && rs.status && rs.status !== 'idle' && (
          <div className="small" style={{ marginTop: 6, color: rs.status === 'error' ? 'var(--danger)' : 'var(--ok)' }}>
            {rs.status === 'error' ? `⚠️ Lỗi lần quét gần nhất: ${rs.lastError || '?'}` : `✓ ${rs.message || 'Đã quét xong'}`}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h2>🔄 Đồng bộ dữ liệu</h2>
          <div className="sub">Quét dữ liệu SocSon ngay trên server FunnyTask (dùng token đã lưu) — không cần mở tab hay cài extension.</div>
        </div>
      </div>

      {isAdmin ? (
      <>
      {/* (a) Lịch quét tự động + Quét thủ công — GỘP 1 thẻ chia đôi (theo thiết kế). */}
      <div className="card">
        <div className="sync-split">
          <ScanScheduleCard />
          <div>
            <h3>🚀 Quét thủ công</h3>
            <div className="small muted" style={{ marginTop: -6, marginBottom: 4 }}>
              Lọc theo khoảng ngày và chạy khi cần — chạy nền trên server, không cần mở tab SocSon.
            </div>
            {ACTIONS.filter((a) => a.group === 'manual').map(renderAction)}
          </div>
        </div>
      </div>

      {/* (b) Tự động theo lịch & chạy nền. */}
      <div className="card">
        <h3>⏱️ Tự động theo lịch &amp; chạy nền</h3>
        <div className="small muted" style={{ marginTop: -6, marginBottom: 4 }}>
          Chạy định kỳ tự động ngay trên server (không cần máy nào mở app/tab) — chỉ bấm khi cần cập nhật/backfill ngay.
        </div>
        {ACTIONS.filter((a) => a.group === 'auto').map(renderAction)}
      </div>

      {/* (c) Token SocSon + Extension — GỘP 1 thẻ chia đôi, đặt CUỐI (theo thiết kế). */}
      <div className="card">
        <div className="sync-split">
          <SocsonServerCard />
          <div>
            <h3>🧩 Extension (dự phòng)</h3>
            <div className="small muted" style={{ marginTop: -6, marginBottom: 8 }}>
              Chỉ dùng khi token SocSon hết hạn và cần quét tạm qua trình duyệt (cài extension FunnyTask + mở tab{' '}
              <b>socsonship.com</b> đã đăng nhập).
            </div>
            {installed === false && (
              <div className="empty">
                Chưa phát hiện extension FunnyTask trên trang này. Không bắt buộc — chỉ cần khi muốn dùng phương án dự phòng.
              </div>
            )}
            {installed && (
              <>
                <div className="field">
                  <label>Địa chỉ server FunnyTask</label>
                  <input value={apiBase} readOnly />
                </div>
                <div className="field">
                  <label>Sync key (khớp SYNC_KEY trên server)</label>
                  <input type="password" value={syncKey} onChange={(e) => setSyncKey(e.target.value)} placeholder="x-sync-key" />
                </div>
                <div className="row" style={{ marginTop: 10 }}>
                  <div className="spacer"></div>
                  <button className="btn" onClick={saveConfig}>
                    {configSaved ? '✓ Đã lưu' : 'Lưu cấu hình'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* (d) NGUỒN thứ 2: pmbox.me (clone SocSon) — token + quét KH riêng, dữ liệu gộp chung. */}
      <div className="card">
        <PmboxServerCard />
      </div>
      </>
      ) : (
        <div className="card">
          <h3>🚀 Quét thủ công</h3>
          <div className="small muted" style={{ marginTop: -6, marginBottom: 8 }}>
            Lọc theo khoảng ngày và bấm Chạy — chạy nền trên server, không cần mở tab SocSon.{' '}
            <b>{me.role === 'TP' ? 'Chỉ quét được đơn/KH của NHÓM bạn.' : 'Chỉ quét được đơn/KH của RIÊNG bạn.'}</b>
          </div>
          {ACTIONS.filter((a) => a.group === 'manual' && a.allowTP).map(renderAction)}
        </div>
      )}
    </>
  );
}
